import { useDrop } from '@brandfolder/react';
import classNames from 'classnames';
import { deepEqual } from 'fast-equals';
import { useRouter } from 'next/router';
import { FunctionComponent, HTMLAttributes, ReactNode, useContext, useRef } from 'react';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';

import { BlockContext } from '@context/blocks';
import { BrandguideContext } from '@context/brandguide';
import { PageContext } from '@context/page';
import { PagesContext } from '@context/pages';
import { PublishContext } from '@context/publish';
import { Block } from '@typings/block';
import { Page } from '@typings/page';
import { moveBlock } from '@utilities/block';
import { bulkPatchPagePositions, getIsHomepage, reorderPages } from '@utilities/page';

interface PagesDropItemProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  block?: Block;
  page?: Page;
}

const UnstyledPagesDropItem: FunctionComponent<PagesDropItemProps> = (props) => {
  const { block, children, page, ...otherProps } = props;

  const router = useRouter();

  const ref = useRef<HTMLDivElement | null>(null);
  const toastRef = useRef('');

  const { blocks, setBlocks } = useContext(BlockContext);
  const { isCname } = useContext(BrandguideContext);
  const { page: currentPage, pages, setPage, setPages } = useContext(PageContext);
  const { movingPage, setMovingPage } = useContext(PagesContext);
  const { publishing, setPublish } = useContext(PublishContext);

  const format = page ? 'page' : block?.pageKey || '';

  const handleSectionMove = (blockKey: string): void => {
    if (block) {
      const { position } = block;
      const droppedBlock = blocks.find((b) => b.key === blockKey) as Block;
      const direction = position > droppedBlock.position ? 'down' : 'up';
      setBlocks([...moveBlock({ ...droppedBlock, position }, blocks, direction)]);
      setPublish(true);
    }
  };

  const handlePageMove = (pageKey: string): void => {
    if (page) {
      const movePage = pages.find((p) => p.key === pageKey);
      if (currentPage && movePage) {
        setMovingPage(movePage);

        const [reorderedPages, pagesToUpdate] = reorderPages(movePage, page, pages);

        const isHome = getIsHomepage(currentPage, isCname, router.asPath);
        const newCurrentPage = reorderedPages.find((p) => p.key === currentPage.key) as Page;

        if (isHome && currentPage.key !== reorderedPages[0].key) {
          setPage(reorderedPages[0]);
        } else if (!isHome && !deepEqual(currentPage, newCurrentPage)) {
          setPage(newCurrentPage);
        }

        const onDroppedComplete = () => {
          toast.dismiss(toastRef.current);
          toast.success('Saved!');
          setMovingPage(null);
        };

        const onDroppedError = () => {
          toast.dismiss(toastRef.current);
          toast.error('Error saving.');
          setMovingPage(null);
        };

        toastRef.current = toast.loading('Saving...');
        setPages(reorderedPages);

        bulkPatchPagePositions({
          pagesToUpdate,
          pages: reorderedPages,
          setPages,
          onComplete: onDroppedComplete,
          onError: onDroppedError
        });
      }
    }
  };

  const disabled = !format || !!movingPage || publishing;

  useDrop({
    disabled,
    format: [format],
    onDropped: (data) => {
      if (block) {
        handleSectionMove(data as string);
      } else if (page) {
        handlePageMove(data as string);
      }
    },
    ref
  });

  return (
    <div
      {...otherProps}
      className={classNames({
        'pages-drop-item': true,
        'pages-drop-item--disabled': disabled,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      ref={ref}
    >
      {children}
    </div>
  );
};

const StyledPagesDropItem = styled(UnstyledPagesDropItem)``;

export const PagesDropItem: FunctionComponent<PagesDropItemProps> = (props) => {
  return <StyledPagesDropItem {...props} />;
};
